import { useEffect, useRef, useState } from 'react'
import { AI_GENERATED_ATTRIBUTE } from './constants'
import { AiGeneratedLabelBadge } from './label-badge'

export type AiGeneratedWatermarkProps = {
	/** Positioning classes for the label image inside the render area. */
	labelClassName?: string
	forceVisible?: boolean
	onDetectedChange?: (detected: boolean) => void
}

/**
 * EU AI-transparency label. Mount inside the `[data-render-me]` element:
 * it watches its parent for descendants stamped with the provenance
 * attribute and overlays the label whenever one is present. Deliberately
 * NOT `data-render-exclude` — it must bake into the exported PNG.
 */
export function AiGeneratedWatermark(
	{ labelClassName = 'right-3 bottom-3', forceVisible = false, onDetectedChange }: AiGeneratedWatermarkProps,
) {
	const hostRef = useRef<HTMLDivElement>(null)
	const [detected, setDetected] = useState(false)
	const visible = forceVisible || detected

	useEffect(() => {
		onDetectedChange?.(detected)
	}, [detected, onDetectedChange])

	useEffect(() => () => onDetectedChange?.(false), [onDetectedChange])

	useEffect(() => {
		const renderRoot = hostRef.current?.parentElement
		if (!renderRoot) {
			return
		}

		const update = () => {
			setDetected(renderRoot.querySelector(`[${AI_GENERATED_ATTRIBUTE}="true"]`) !== null)
		}
		update()

		const observer = new MutationObserver(update)
		observer.observe(renderRoot, {
			subtree: true,
			childList: true,
			attributes: true,
			attributeFilter: [AI_GENERATED_ATTRIBUTE],
		})
		return () => observer.disconnect()
	}, [])

	return (
		<div ref={hostRef} className="absolute inset-0 pointer-events-none z-[60]">
			{visible && (
				<div className={`absolute w-[22%] pointer-events-auto ${labelClassName}`}>
					<AiGeneratedLabelBadge />
				</div>
			)}
		</div>
	)
}
